import { Dispatch, SetStateAction } from 'react'
import { Element, FieldConfiguration } from '@phrasee/phrasee-typings'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { SelectValue } from 'common/components/BaseSelect'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import useGetMergeTagsQuery from 'features/admin/accountSettings/api/queries/useGetMergeTagsQuery'
import useGenerateBriefMutation from 'features/unifiedFlow/api/mutations/useGenerateBriefMutation'
import useGenerateCustomBriefMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomBriefMutation'
import useGenerateCustomVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomVariantsMutation'
import useGenerateVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateVariantsMutation'
import { ContentWithProgress } from 'features/unifiedFlow/api/queries/useGetContentQuery'
import { userInputFormHasChanged } from 'features/unifiedFlow/store/unifiedFlowSlice'

import useWebsocketMessage from '../../../hooks/websocketMessage'
import { getWsTopic } from '../../../hooks/websocketMessage/helpers'
import { FormSubmitAction } from '../BriefPage'

export type CustomerJourney = {
  customerJourneyTitle: string
  [key: string]: unknown
}

export const useBriefGeneration = ({
  content,
  selectedElement,
  isGlobalSettings,
  isOptimized,
  shouldUseTopics,
  userInputForm,
  hasVariants,
  isModalOpenRef,
  setResetWarningModalState,
  selectedCustomerJourney,
}: {
  content: ContentWithProgress | undefined
  selectedElement: Element | undefined
  isGlobalSettings: boolean
  isOptimized: boolean
  shouldUseTopics: boolean | undefined
  userInputForm: FieldConfiguration[] | undefined
  hasVariants: boolean | undefined
  isModalOpenRef: React.MutableRefObject<boolean>
  setResetWarningModalState: Dispatch<
    SetStateAction<{
      isOpen: boolean
      onConfirm?: () => void
    }>
  >
  selectedCustomerJourney?: CustomerJourney
}) => {
  const dispatch = useAppDispatch()
  const flags = useFlags()
  const { sendWebsocketMessage } = useWebsocketMessage()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const selectedTopics = useAppSelector(
    (state) => state.unifiedFlow.selectedTopics
  )

  const { data: mergeTags } = useGetMergeTagsQuery()

  const generateBriefMutation = useGenerateBriefMutation()
  const generateCustomBriefMutation = useGenerateCustomBriefMutation()
  const generateVariantsMutation = useGenerateVariantsMutation()
  const generateCustomVariantsMutation = useGenerateCustomVariantsMutation()

  const generateBrief: FormSubmitAction = async (formValues) => {
    if (!content || !userInputForm) {
      return
    }

    if (hasVariants && isGlobalSettings && !isModalOpenRef.current) {
      setResetWarningModalState({
        isOpen: true,
        onConfirm: () => generateBrief(formValues),
      })
      return
    }

    const wsTopic = isGlobalSettings
      ? getWsTopic({
          data: { contentId: content._id },
          action: 'generateGlobalBrief',
        })
      : getWsTopic({
          data: {
            contentId: content._id,
            elementId: selectedElement!.element_id,
          },
          action: 'generateElementBrief',
        })

    if (userInputForm) {
      userInputForm.forEach((field) => {
        if (formValues[field.name] != null) {
          field.value = formValues[field.name]
        } else {
          delete field.value
        }
      })

      // Add or update customer journey field in userInputForm (only for new flow)
      if (selectedCustomerJourney) {
        const customerJourneyField = userInputForm.find(
          (field) => field.name === 'customer_journey'
        )
        const customerJourneyValue =
          selectedCustomerJourney.customerJourneyTitle

        if (customerJourneyField) {
          customerJourneyField.value = customerJourneyValue
        } else {
          // Add customer journey field if it doesn't exist
          const maxOrdinal = Math.max(
            ...userInputForm.map((field) => field.ordinal || 0),
            0
          )
          userInputForm.push({
            ordinal: maxOrdinal + 1,
            name: 'customer_journey',
            type: 'text' as any,
            label: 'Customer Journey',
            required: false,
            value: customerJourneyValue,
          })
        }
      }
    }

    const selectedBrandVoice = formValues.brand_voice as SelectValue | undefined
    const selectedMergeTagIds = formValues.merge_tags as string[] | undefined
    const selectedMergeTags =
      mergeTags?.filter((mergeTag) =>
        selectedMergeTagIds?.includes(mergeTag.id)
      ) || []

    if (!isGlobalSettings && selectedElement?.element_id) {
      sendWebsocketMessage({
        action: 'generateElementBrief',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })

      generateCustomBriefMutation.mutate({
        accountId,
        contentId: content._id,
        elementId: selectedElement?.element_id,
        userInputForm,
        wsTopic,
        ...(!isOptimized && { brandVoiceId: selectedBrandVoice?.value }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    } else {
      sendWebsocketMessage({
        action: 'generateGlobalBrief',
        data: {
          contentId: content._id,
        },
        subscriptionAction: 'subscribe',
      })
      generateBriefMutation.mutate({
        accountId,
        contentId: content._id,
        userInputForm,
        wsTopic,
        ...(!isOptimized && { brandVoiceId: selectedBrandVoice?.value }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    }
  }

  const generateVariants: FormSubmitAction = async (formValues) => {
    if (!content) {
      return
    }

    if (hasVariants && isGlobalSettings && !isModalOpenRef.current) {
      setResetWarningModalState({
        isOpen: true,
        onConfirm: () => generateVariants(formValues),
      })
      return
    }

    const selectedBrandVoice = formValues.brand_voice as SelectValue | undefined
    const selectedMergeTagIds = formValues.merge_tags as string[] | undefined
    const selectedMergeTags =
      mergeTags?.filter((mergeTag) =>
        selectedMergeTagIds?.includes(mergeTag.id)
      ) || []

    if (userInputForm) {
      userInputForm.forEach((field) => {
        if (formValues[field.name] != null) {
          field.value = formValues[field.name]
        } else {
          delete field.value
        }
      })
    }

    const wsTopic = !isGlobalSettings
      ? getWsTopic({
          data: {
            contentId: content._id,
            elementId: selectedElement!.element_id,
          },
          action: 'generateElementVariants',
        })
      : ''

    dispatch(userInputFormHasChanged({ hasChanged: false }))

    if (!isGlobalSettings && selectedElement?.element_id) {
      sendWebsocketMessage({
        action: 'generateElementVariants',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })

      generateCustomVariantsMutation.mutate({
        accountId,
        topics: selectedTopics,
        contentId: content._id,
        elementId: selectedElement?.element_id,
        wsTopic,
        ...(!isOptimized && { brandVoiceId: selectedBrandVoice?.value }),
        ...(!shouldUseTopics && { userInputForm }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    } else {
      content.elements.forEach((element) => {
        sendWebsocketMessage({
          action: 'generateElementVariants',
          data: {
            contentId: content._id,
            elementId: element.element_id,
          },
          subscriptionAction: 'subscribe',
        })
      })

      generateVariantsMutation.mutate({
        topics: selectedTopics,
        accountId,
        wsTopic,
        contentId: content._id,
        ...(!isOptimized && { brandVoiceId: selectedBrandVoice?.value }),
        ...(!shouldUseTopics && { userInputForm }),
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    }
  }

  const isGeneratingBrief =
    generateBriefMutation.isPending || generateCustomBriefMutation.isPending

  const generateContentFromImport = (
    id: string,
    type: 'campaigns' | 'canvas',
    mergeTagIds?: string[]
  ) => {
    if (!content) {
      return
    }

    const selectedMergeTags =
      mergeTags?.filter((tag) => mergeTagIds?.includes(tag.id)) || []

    const idField =
      type === 'canvas' ? { brazeCanvasId: id } : { brazeCampaignId: id }

    dispatch(userInputFormHasChanged({ hasChanged: false }))

    if (!isGlobalSettings && selectedElement?.element_id) {
      const wsTopic = getWsTopic({
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        action: 'generateElementVariants',
      })

      sendWebsocketMessage({
        action: 'generateElementVariants',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })

      generateCustomVariantsMutation.mutate({
        accountId,
        topics: selectedTopics,
        contentId: content._id,
        elementId: selectedElement.element_id,
        wsTopic,
        ...idField,
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    } else {
      content.elements.forEach((element) => {
        sendWebsocketMessage({
          action: 'generateElementVariants',
          data: {
            contentId: content._id,
            elementId: element.element_id,
          },
          subscriptionAction: 'subscribe',
        })
      })

      generateVariantsMutation.mutate({
        accountId,
        topics: selectedTopics,
        wsTopic: '',
        contentId: content._id,
        ...idField,
        ...(flags.showMergeTags && { mergeTags: selectedMergeTags }),
      })
    }
  }

  return {
    generateBrief,
    generateContentFromImport,
    generateVariants,
    isGeneratingBrief,
  }
}
